/**
 * Copiar e compartilhar texto — com os tres caminhos que sobrevivem ao pior
 * ambiente do projeto, o WebView do app Android.
 *
 * 1. Ponte nativa (`AlmoxNativo`), quando o app a expoe: e a unica que nao
 *    depende de permissao nem de contexto seguro dentro do WebView.
 * 2. `navigator.clipboard`, no navegador com HTTPS.
 * 3. `document.execCommand('copy')` sobre um textarea escondido — obsoleto,
 *    mas e o que funciona em WebView antigo.
 *
 * Nenhum deles e garantido em todo aparelho, entao quem chama recebe `false`
 * e mostra o texto para copia manual em vez de deixar o usuario no escuro.
 */

interface PonteNativa {
  copiar?: (texto: string) => void;
  compartilhar?: (texto: string) => void;
}

function ponte(): PonteNativa | null {
  const nativo = (window as unknown as { AlmoxNativo?: PonteNativa }).AlmoxNativo;
  return nativo && typeof nativo === 'object' ? nativo : null;
}

/** True quando o app Android pode abrir o menu de compartilhar (WhatsApp). */
export function podeCompartilhar(): boolean {
  return typeof ponte()?.compartilhar === 'function' || typeof navigator.share === 'function';
}

export async function copiarTexto(texto: string): Promise<boolean> {
  const nativo = ponte();
  if (typeof nativo?.copiar === 'function') {
    try {
      nativo.copiar(texto);
      return true;
    } catch {
      // Cai para os caminhos do navegador.
    }
  }

  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(texto);
      return true;
    } catch {
      // Sem permissao ou sem contexto seguro: tenta o modo antigo.
    }
  }

  return copiarPeloTextarea(texto);
}

/**
 * Abre o menu de compartilhamento do sistema (onde o WhatsApp aparece). No
 * navegador sem Web Share API devolve `false` e a tela oferece copiar.
 */
export async function compartilharTexto(texto: string, titulo: string): Promise<boolean> {
  const nativo = ponte();
  if (typeof nativo?.compartilhar === 'function') {
    try {
      nativo.compartilhar(texto);
      return true;
    } catch {
      // Segue para a API do navegador.
    }
  }

  if (typeof navigator.share === 'function') {
    try {
      await navigator.share({ title: titulo, text: texto });
      return true;
    } catch (erro) {
      // Cancelar o menu nao e falha: nao adianta oferecer outra coisa.
      if ((erro as { name?: string }).name === 'AbortError') return true;
    }
  }
  return false;
}

function copiarPeloTextarea(texto: string): boolean {
  try {
    const campo = document.createElement('textarea');
    campo.value = texto;
    // Fora da tela, mas focavel: `display:none` impede a selecao.
    campo.style.position = 'fixed';
    campo.style.top = '-1000px';
    campo.setAttribute('readonly', 'readonly');
    document.body.appendChild(campo);
    campo.select();
    campo.setSelectionRange(0, texto.length);
    const copiou = document.execCommand('copy');
    document.body.removeChild(campo);
    return copiou;
  } catch {
    return false;
  }
}
